import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Input,
  Select,
  Option,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Tooltip,
} from '@mui/joy';
import {
  SearchOutlined,
  VisibilityOutlined,
  SupervisorAccountOutlined,
} from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { AdminAPI } from '../../lib/apiAdmin';
import type { AdminOrganizationListItem, OrgStaleness } from '../../types/admin';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';
import { orderingParam } from '../../components/common/tableSort';
import type { SortState } from '../../components/common/tableSort';
import { StatsCard } from '../../components/dashboard/StatsCard';
import { STALENESS_META, formatCurrency, statusChipColor } from './adminFormat';
import { AdminQueryError } from '../../components/admin/AdminQueryError';

const PAGE_SIZE = 20;

const StalenessDot: React.FC<{ staleness: OrgStaleness }> = ({ staleness }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: STALENESS_META[staleness].dot,
      flexShrink: 0,
    }}
  />
);

export const AdminOrganizationsDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [staleness, setStaleness] = useState<OrgStaleness | ''>('');
  const [hasTickets, setHasTickets] = useState(false);
  const [ordering, setOrdering] = useState('name');

  // The header controls and the Select drive the same server-side `ordering`,
  // so the two can never disagree about how the list is sorted.
  const SORT_FIELDS = ['name', 'created_at', 'mrr', 'user_count', 'open_ticket_count', 'last_activity_at'];
  const COLUMN_FOR_FIELD: Record<string, string> = {
    name: 'name', mrr: 'mrr', user_count: 'users',
    last_activity_at: 'last_active', open_ticket_count: 'tickets',
  };
  const sortState: SortState | null = (() => {
    const desc = ordering.startsWith('-');
    const field = desc ? ordering.slice(1) : ordering;
    const key = COLUMN_FOR_FIELD[field];
    return key && SORT_FIELDS.includes(field)
      ? { key, direction: desc ? 'desc' : 'asc' }
      : null;
  })();
  const FIELD_FOR_COLUMN: Record<string, string> = Object.fromEntries(
    Object.entries(COLUMN_FOR_FIELD).map(([f, k]) => [k, f])
  );
  const [page, setPage] = useState(1);
  const [urlParams, setUrlParams] = useSearchParams();
  const accountView = urlParams.get('view') || '';
  const setAccountView = (value: string) => { setPage(1); setUrlParams(previous => {const next = new URLSearchParams(previous);if (value) next.set('view', value); else next.delete('view');return next;}); };
  const setSelectedOrgId = React.useCallback((id: string | null) => setUrlParams(previous => {const next = new URLSearchParams(previous); if(id) next.set('organization', id); else next.delete('organization'); return next;}, {replace:true}), [setUrlParams]);

  // Debounce the search box (and reset to page 1 when the term changes).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: summary, error: summaryError, refetch: reloadSummary } = useQuery({
    queryKey: ['admin-summary'],
    queryFn: () => AdminAPI.getPlatformSummary(),
  });

  const {
    data: orgPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-orgs', { search, status, staleness, hasTickets, ordering, page, accountView }],
    queryFn: () =>
      AdminAPI.getOrganizations({
        search,
        account_view: accountView,
        status,
        staleness,
        has_open_tickets: hasTickets,
        ordering,
        page,
        page_size: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = orgPage?.results ?? [];
  const totalCount = orgPage?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const columns = useMemo<ResponsiveColumn<AdminOrganizationListItem>[]>(
    () => [
      {
        key: 'name',
        sortKey: 'name',
        header: 'Account',
        mobileVariant: 'header',
        render: (o) => (
          <Box>
            <Typography
              component="button" type="button"
              level="title-sm"
              sx={{ border:0, p:0, bgcolor:'transparent', color:'text.primary', font:'inherit', textAlign:'left', cursor:'pointer', '&:hover':{textDecoration:'underline'}, '&:focus-visible':{outline:'2px solid',outlineColor:'primary.500'} }}
              onClick={() => setSelectedOrgId(o.id)}
            >
              {o.name}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {o.owner_email}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'plan',
        header: 'Plan',
        render: (o) => (
          <Chip size="sm" variant="soft" color={statusChipColor(o.subscription_status, o.is_free)}>
            {o.plan_label}
          </Chip>
        ),
      },
      {
        key: 'onboarding', header: 'Onboarding & owner', mobileVariant: 'body',
        render: (o) => <Box sx={{ display: 'grid', gap: .5 }}><Chip size="sm" color={o.cx?.onboarding_status === 'blocked' ? 'danger' : o.cx?.onboarding_status === 'complete' ? 'success' : 'neutral'}>{o.cx?.onboarding_status === 'complete' ? 'Ready' : o.cx?.onboarding_status === 'blocked' ? 'Blocked' : `${o.cx?.completed_steps ?? 0}/${o.cx?.total_steps ?? 0} steps`}</Chip><Typography level="body-xs">{o.cx?.owner_name || 'Unassigned'}</Typography>{o.cx?.next_step && <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{o.cx.next_step}</Typography>}</Box>,
      },
      {
        key: 'next_action', header: 'Next action', mobileVariant: 'body',
        render: (o) => <Box sx={{ maxWidth: 260 }}><Typography level="body-sm">{o.cx?.next_task || (o.cx?.customer_id ? 'No open follow-up' : 'Start onboarding')}</Typography>{!!o.cx?.overdue_tasks && <Chip size="sm" color="danger">{o.cx.overdue_tasks} overdue</Chip>}{o.cx?.next_due && <Typography level="body-xs">Due {new Date(o.cx.next_due).toLocaleDateString()}</Typography>}{o.cx?.health_band && <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Health: {o.cx.health_band.replace('_', ' ')} · {o.cx.health_score}/100</Typography>}</Box>,
      },
      {
        key: 'mrr',
        sortKey: 'mrr',
        header: 'MRR',
        render: (o) => (
          <Typography level="body-sm">{formatCurrency(o.mrr)}/mo</Typography>
        ),
      },
      {
        key: 'users',
        sortKey: 'user_count',
        header: 'Users',
        render: (o) => <Typography level="body-sm">{o.user_count}</Typography>,
      },
      {
        key: 'last_active',
        sortKey: 'last_activity_at',
        header: 'Last Active',
        render: (o) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
            <StalenessDot staleness={o.staleness} />
            <Typography level="body-sm">
              {o.last_activity_at
                ? `${formatDistanceToNowStrict(new Date(o.last_activity_at))} ago`
                : 'Never'}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'tickets',
        sortKey: 'open_ticket_count',
        header: 'Open Tickets',
        render: (o) =>
          o.open_ticket_count > 0 ? (
            <Chip size="sm" variant="soft" color={o.open_ticket_count > 3 ? 'danger' : 'warning'}>
              {o.open_ticket_count}
            </Chip>
          ) : (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          ),
      },
    ],
    [setSelectedOrgId]
  );

  return (
    <Box>
      <AdminQueryError error={summaryError} retry={reloadSummary} message="Platform totals could not be loaded." />
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer' }}>Portfolio totals · {summary?.total_organizations ?? '—'} accounts · {summary?.trial_count ?? '—'} trials</summary>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
          gap: 2,
          mb: 3,
          '& .MuiCard-root':{p:{xs:1.25,md:2}},
          '& .MuiCardContent-root > .MuiBox-root':{display:{xs:'none',md:'flex'}},
          '& h2':{fontSize:{xs:'xl',md:'xxl'},mb:0.5},
          '& .MuiCardContent-root > p':{fontSize:{xs:'xs',md:'sm'},mb:0.5},
        }}
      >
        <StatsCard
          title="Organizations"
          value={summary?.total_organizations ?? '—'}
          change={summary ? `${summary.active_count} active · ${summary.trial_count} trial` : "Totals unavailable"}
          changeType="neutral"
          icon="🏢"
          color="primary"
        />
        <StatsCard
          title="Total MRR"
          value={summary ? formatCurrency(summary.total_mrr) : '—'}
          change={summary ? `active paying only · avg ${formatCurrency(summary.avg_mrr_per_org)}/org` : 'Totals unavailable'}
          changeType="positive"
          icon="💵"
          color="success"
        />
        <StatsCard
          title="Active Users"
          value={summary?.total_active_users ?? '—'}
          change={summary ? `${summary.new_orgs_this_month} new orgs (30d)` : 'Totals unavailable'}
          changeType="neutral"
          icon="👥"
          color="info"
        />
        <Box
          role="button" tabIndex={0} aria-label="Filter stale accounts" aria-pressed={staleness === 'stale'}
          onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();setStaleness(s=>s==='stale'?'':'stale');setPage(1);}}}
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            setStaleness((s) => (s === 'stale' ? '' : 'stale'));
            setPage(1);
          }}
        >
          <StatsCard
            title="Stale Accounts"
            value={summary?.stale_org_count ?? '—'}
            change={staleness === 'stale' ? 'Filtered ✓' : 'View stale →'}
            changeType={(summary?.stale_org_count ?? 0) > 0 ? 'negative' : 'neutral'}
            icon="⚠️"
            color="warning"
          />
        </Box>
        <Box role="button" tabIndex={0} aria-label="View support tickets" onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();navigate('/manage/support');}}} sx={{ cursor: 'pointer' }} onClick={() => navigate('/manage/support')}>
          <StatsCard
            title="Open Tickets"
            value={summary?.open_ticket_count ?? '—'}
            change="View support →"
            changeType="neutral"
            icon="🎫"
            color="danger"
          />
        </Box>
      </Box>

      </details>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }} role="group" aria-label="Account views">
        {[["", "All accounts"], ["attention", "Needs attention"], ["trial", "Free trials"], ["onboarding", "Onboarding"], ["free", "Free accounts"]].map(([value, label]) => <Button key={value} size="sm" aria-pressed={accountView === value} variant={accountView === value ? 'solid' : 'outlined'} color={accountView === value ? 'primary' : 'neutral'} onClick={() => setAccountView(value)}>{label}</Button>)}
      </Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Input
          placeholder="Search account name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startDecorator={<SearchOutlined />}
          sx={{ flex: '1 1 240px' }}
        />
        <Select
          placeholder="Status"
          value={status}
          onChange={(_, v) => {
            setStatus(v ?? '');
            setPage(1);
          }}
          sx={{ minWidth: 140 }}
        >
          <Option value="">All statuses</Option>
          <Option value="active">Active</Option>
          <Option value="trialing">Trialing</Option>
          <Option value="past_due">Past Due</Option>
          <Option value="canceled">Canceled</Option>
          <Option value="unpaid">Unpaid</Option>
        </Select>
        <Select
          placeholder="Staleness"
          value={staleness}
          onChange={(_, v) => {
            setStaleness((v as OrgStaleness | '') ?? '');
            setPage(1);
          }}
          sx={{ minWidth: 140 }}
        >
          <Option value="">All activity</Option>
          <Option value="active">Active</Option>
          <Option value="at_risk">At Risk</Option>
          <Option value="stale">Stale</Option>
        </Select>
        <Select
          value={ordering}
          onChange={(_, v) => {
            setOrdering(v ?? 'name');
            setPage(1);
          }}
          sx={{ minWidth: 160 }}
        >
          <Option value="name">Name (A–Z)</Option>
          <Option value="-created_at">Newest</Option>
          <Option value="-mrr">MRR (high→low)</Option>
          <Option value="-user_count">Users (high→low)</Option>
          <Option value="-open_ticket_count">Open tickets (high→low)</Option>
          <Option value="last_activity_at">Least recently active</Option>
          <Option value="cx_health_score">Health (lowest first)</Option>
          <Option value="-cx_open_tasks">Most open tasks</Option>
        </Select>
        <Chip
          variant={hasTickets ? 'solid' : 'outlined'}
          color={hasTickets ? 'primary' : 'neutral'}
          onClick={() => {
            setHasTickets((v) => !v);
            setPage(1);
          }}
          sx={{ cursor: 'pointer' }}
        >
          Has open tickets
        </Chip>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Could not load accounts.</Alert>
      ) : (
        <Box sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <ResponsiveTable<AdminOrganizationListItem>
            rows={rows}
            getRowKey={(o) => o.id}
            columns={columns}
        sort={sortState}
        onSortChange={(next) => {
          // Unsorting returns to the API's own default rather than no order.
          setOrdering(orderingParam(next, (k) => FIELD_FOR_COLUMN[k]) ?? 'name');
          setPage(1);
        }}
            rowActions={(o) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="View details">
                  <IconButton aria-label={`Open ${o.name} profile`} size="sm" variant="plain" onClick={() => setSelectedOrgId(o.id)}>
                    <VisibilityOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Impersonate a user">
                  <IconButton aria-label="Sign in as a user" size="sm" variant="plain" onClick={() => setUrlParams(previous => {const next = new URLSearchParams(previous);next.set('organization', o.id);next.set('profileTab', 'people');return next;})}>
                    <SupervisorAccountOutlined />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            emptyState={
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
                  No accounts match your filters.
                </Typography>
              </Box>
            }
          />

          {/* Pagination */}
          {totalCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {totalCount} account{totalCount === 1 ? '' : 's'} · page {page} of {totalPages}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="sm"
                  variant="outlined"
                  color="primary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  color="primary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}


    </Box>
  );
};

export default AdminOrganizationsDashboard;
